// The sign-in page's swallowtails: a small flock flying in a space behind
// the panel, seen as if through a window, and the code that draws them.
// ButterflyField turns the drawing into ASCII.
//
// World units are wingspans. The camera sits at the origin looking along +z,
// with x right and y up. A view's hwu and hhu are the frame's half width and
// half height at depth 1, so at depth z the frame spans x in [-hwu*z, hwu*z]
// and y in [-hhu*z, hhu*z]. Pointer positions are in the same frame's
// normalized coordinates: x and y in [-1, 1], y up.

const TAU = Math.PI * 2;

// The depths they keep to: close enough to fill the panel without crowding it.
export const Z_NEAR = 2.4;
export const Z_FAR = 4.6;
const Z_MID = (Z_NEAR + Z_FAR) / 2;
const Z_HALF = (Z_FAR - Z_NEAR) / 2;

export const MAX_TURN = 0.42; // rad/s when calm: wide, unhurried arcs
export const MAX_TURN_STARTLED = 0.85;
const YAW_ACCEL = 0.5; // rad/s^2, so a turn eases in instead of snapping
const CRUISE = 0.22; // wingspans per second
const SPACING = 1.0; // wingspans they keep from each other
const LOOKAHEAD_S = 2;

// The forewing's angle above the body's horizontal plane over one beat. It
// lingers open, just below flat, and passes quickly over the top.
const STROKE_MID = 0.45;
const STROKE_AMP = 0.8;
const GLIDE_ANGLE = 0.16; // a glide holds the wings in a shallow V
const BOB = 0.035; // wingspans the body rises on each downstroke

const STARTLE_SPEED = 1.2; // pointer speed that startles, frame half-widths per second
const STARTLE_RADIUS = 0.35;
const FEAR_DECAY = 0.4; // per second
const CURIOUS_AFTER_S = 1.8; // a pointer resting this long draws one over

// at = [screen x, screen y, depth], yaw = heading in the x-z plane (0 is right).
const FLOCK = [
    { size: 1.1, hz: 1.0, at: [-0.2, 0.15, 3.0], yaw: 0.25 },
    { size: 0.95, hz: 1.15, at: [0.35, -0.3, 4.2], yaw: 2.7 },
];

export function createFlock(view, { seed = 7, still = false } = {}) {
    const rand = mulberry32(seed);
    const butterflies = FLOCK.map((f, i) => {
        const [nx, ny, z] = f.at;
        const gliding = still || i % 2 === 0;
        return {
            size: f.size,
            hz: f.hz,
            seed: rand() * 100,
            p: { x: nx * view.hwu * z, y: ny * view.hhu * z, z },
            yaw: f.yaw,
            yawRate: 0,
            speed: CRUISE,
            vy: 0,
            phase: gliding ? Math.PI : rand() * TAU,
            gliding,
            wantGlide: false,
            glide: gliding ? 1 : 0,
            boutLeft: between(rand, 0.8, 2.4),
            fear: 0,
            fleeX: 1,
            fleeY: 1,
        };
    });
    return { butterflies, rand, curious: null, curiousZ: Z_MID };
}

export function screenPosition(b, view) {
    return [b.p.x / (view.hwu * b.p.z), b.p.y / (view.hhu * b.p.z)];
}

// pointer: { x, y, present, speed, restS } or null.
export function stepFlock(flock, dt, t, view, pointer = null) {
    if (!(pointer?.present && pointer.restS > CURIOUS_AFTER_S)) {
        flock.curious = null;
    } else if (!flock.curious) {
        flock.curious = nearestOnScreen(flock.butterflies, view, pointer);
        flock.curiousZ = clamp(flock.curious.p.z, Z_NEAR + 0.4, Z_MID);
    }
    for (const b of flock.butterflies) step(b, flock, dt, t, view, pointer);
}

function step(b, flock, dt, t, view, pointer) {
    const p = b.p;
    const curious = flock.curious === b;

    if (pointer?.present && pointer.speed > STARTLE_SPEED) {
        const [sx, sy] = screenPosition(b, view);
        const near = 1 - Math.hypot(sx - pointer.x, sy - pointer.y) / STARTLE_RADIUS;
        if (near > b.fear) {
            b.fear = near;
            b.fleeX = sx >= pointer.x ? 1 : -1;
            b.fleeY = sy >= pointer.y ? 1 : -1;
        }
    }
    b.fear = Math.max(0, b.fear - dt * FEAR_DECAY);

    const target = curious ? orbitTarget(flock, t, view, pointer) : homeTarget(b, t, view);

    // Flapping bouts and glides take turns. A glide only begins as the wings
    // come open, so they never freeze mid-beat.
    b.boutLeft -= dt;
    if (b.gliding) {
        if (b.boutLeft <= 0 || b.fear > 0.15 || p.y < target.y - 0.6) {
            b.gliding = false;
            b.boutLeft = between(flock.rand, 1.8, 3.4);
        }
    } else if (b.boutLeft <= 0 && b.fear < 0.15) {
        b.wantGlide = true;
    }
    if (!b.gliding) {
        const before = b.phase;
        b.phase += TAU * b.hz * (1 + 0.6 * b.fear) * dt;
        const open = Math.PI + TAU * Math.floor((b.phase - Math.PI) / TAU);
        if (b.wantGlide && open > before) {
            b.phase = open;
            b.gliding = true;
            b.wantGlide = false;
            b.boutLeft = curious ? between(flock.rand, 2.5, 4.5) : between(flock.rand, 1.4, 3.0);
        }
    }
    b.glide += ((b.gliding ? 1 : 0) - b.glide) * Math.min(1, dt * 2.2);

    // Steering: meander freely in the middle of the frame, and turn back
    // toward a drifting point of its own as it nears an edge. Edges are judged
    // where it will soon be, so a calm turn starts in time: the sides a second
    // ahead, loose enough that wings dip past them, and the camera further ahead.
    const ax = p.x + Math.cos(b.yaw) * b.speed;
    const az = p.z + Math.sin(b.yaw) * b.speed * LOOKAHEAD_S;
    const edge = Math.max(
        Math.abs(ax) / (view.hwu * p.z * 0.8),
        Math.abs(p.y) / (view.hhu * p.z * 0.75),
        az < Z_MID ? (Z_MID - az) / (Z_HALF * 0.6) : (az - Z_MID) / Z_HALF,
    );
    const hold = curious ? 1 : smoothstep(0.55, 1, edge);
    const tooNear = smoothstep(Z_NEAR + 0.5, Z_NEAR - 0.2, az);

    // Each influence votes for a heading, weighted, and it turns toward their
    // sum. Summing headings rather than turn rates keeps two sensible votes,
    // like "back to your point" and "away from the camera", from cancelling.
    let vx = 0, vz = 0;
    const vote = (yaw, w) => { vx += Math.cos(yaw) * w; vz += Math.sin(yaw) * w; };
    vote(b.yaw + noise(t * 0.09, b.seed) * 0.35, 1 - hold);
    vote(Math.atan2(target.z - p.z, target.x - p.x), 2 * hold);
    vote(Math.PI / 2, 3 * tooNear);
    let lift = clamp((target.y - p.y) * 0.25, -0.09, 0.09);

    for (const o of flock.butterflies) {
        if (o === b) continue;
        const dx = p.x - o.p.x, dy = p.y - o.p.y, dz = p.z - o.p.z;
        const d = Math.hypot(dx, dy, dz);
        if (d < SPACING) {
            // Keeping clear of the camera matters more than keeping spaced.
            const w = (1 - d / SPACING) ** 2 * (1 - tooNear);
            vote(Math.atan2(dz, dx), 2 * w);
            lift += (dy >= 0 ? 1 : -1) * 0.08 * w;
        }
    }
    if (b.fear > 0) {
        vote(Math.atan2(Math.sin(b.yaw) * 0.6, b.fleeX), 3 * b.fear);
        lift += b.fleeY * 0.18 * b.fear;
    }
    const heading = vx || vz ? Math.atan2(vz, vx) : b.yaw;
    // Turning away from the glass, or from a startle, may be a little quicker.
    const maxTurn = MAX_TURN + (MAX_TURN_STARTLED - MAX_TURN) * Math.max(b.fear, 0.35 * tooNear);
    const turn = clamp(turnToward(b.yaw, heading) * 1.1, -1, 1) * maxTurn;
    const accel = YAW_ACCEL * (1 + 2 * b.fear) * dt;
    b.yawRate += clamp(turn - b.yawRate, -accel, accel);
    b.yaw += b.yawRate * dt;

    // Each downstroke gives a small surge; a glide coasts a little slower
    // and sinks, a flapping bout climbs.
    const downstroke = Math.max(0, Math.sin(beat(b.phase))) * (1 - b.glide);
    // Heading toward the camera when close, it slows, as if pausing at the glass.
    const near = 1 - 0.6 * Math.max(0, -Math.sin(b.yaw)) * smoothstep(Z_NEAR + 0.6, Z_NEAR - 0.2, az);
    const cruise = CRUISE * (0.85 + 0.15 * b.size) * near * (curious ? 0.7 : 1) * (1 + 0.7 * b.fear) * (1 - 0.12 * b.glide);
    b.speed += (cruise * (1 + 0.06 * downstroke) - b.speed) * Math.min(1, dt * 1.5);
    b.vy += (lift + 0.035 * (1 - b.glide) - 0.05 * b.glide - b.vy) * Math.min(1, dt * 1.2);

    p.x += Math.cos(b.yaw) * b.speed * dt;
    p.z += Math.sin(b.yaw) * b.speed * dt;
    p.y += b.vy * dt;
}

function homeTarget(b, t, view) {
    const z = Z_MID + Z_HALF * 0.55 * Math.sin(t * 0.031 + b.seed * 3.1);
    return {
        x: view.hwu * z * 0.5 * Math.sin(t * 0.043 + b.seed * 1.7),
        y: view.hhu * z * 0.5 * Math.sin(t * 0.057 + b.seed * 2.3),
        z,
    };
}

// A slow circle around the resting pointer, at the depth the curious one
// was flying when it noticed.
function orbitTarget(flock, t, view, pointer) {
    const z = flock.curiousZ;
    const a = t * 0.35;
    return {
        x: pointer.x * view.hwu * z + 0.45 * Math.cos(a),
        y: pointer.y * view.hhu * z + 0.08,
        z: z + 0.45 * Math.sin(a),
    };
}

function nearestOnScreen(butterflies, view, pointer) {
    let best = butterflies[0], bestD = Infinity;
    for (const b of butterflies) {
        const [x, y] = screenPosition(b, view);
        const d = Math.hypot(x - pointer.x, y - pointer.y);
        if (d < bestD) { best = b; bestD = d; }
    }
    return best;
}

const beat = (phase) => phase + 0.35 * Math.sin(phase);
const strokeAngle = (phase) => STROKE_MID + STROKE_AMP * Math.cos(beat(phase));

// Wings bend: along the span the tip trails the root through each beat, and
// in a glide the tips curl a little higher than the roots.
const FLEX = 0.6; // phase the tip lags the root, per wingspan of span
const CURL = 0.3; // extra glide angle at the tip, per wingspan of span

// fore(s) and hind(s) give each wing's angle at span s.
export function wingPose(b, t) {
    const g = b.glide;
    const glideAngle = GLIDE_ANGLE + 0.05 * Math.sin(t * 2.1 + b.seed);
    const wave = beat(b.phase);
    return {
        fore: (s) => strokeAngle(b.phase - FLEX * s) * (1 - g) + (glideAngle + CURL * s) * g,
        hind: (s) =>
            (STROKE_MID + (strokeAngle(b.phase - 0.18 - FLEX * s) - STROKE_MID) * 0.92) * (1 - g) +
            (glideAngle - 0.03 + CURL * s) * g,
        bob: -BOB * Math.cos(wave - 0.5) * (1 - g),
        pitch: 0.18 + 0.06 * Math.cos(wave) * (1 - g) + clamp(b.vy * 0.6, -0.15, 0.15),
        // Banks into a turn: a left turn (yaw rising) lowers the left wing.
        bank: clamp(-b.yawRate * b.speed * 2, -0.35, 0.35),
    };
}

// ── Drawing ────────────────────────────────────────────────────────────

const LIGHT = unit([-0.35, 0.85, -0.4]); // from above, left, and the viewer's side
export const BONE = [236, 226, 208];
// Each butterfly is drawn as seen from a little above, the way you look out
// and down through a window, so glides show the wings' upper sides instead
// of their edges. Positions are not tilted: only how each one is turned.
const VIEW_TILT = 0.6;

// Swallowtail outlines for the right-hand wings, as [span, chord] in
// wingspans: span runs out from the body, chord runs toward the head. They
// are drawn as smooth curves through these points.
const FOREWING = [
    [0.03, 0.03], [0.08, 0.07], [0.15, 0.108], [0.23, 0.138], [0.31, 0.158],
    [0.38, 0.166], [0.435, 0.16], [0.472, 0.142], [0.494, 0.115], [0.5, 0.082],
    [0.49, 0.045], [0.468, 0.008], [0.436, -0.028], [0.396, -0.058], [0.35, -0.078],
    [0.3, -0.088], [0.24, -0.084], [0.18, -0.07], [0.12, -0.05], [0.07, -0.028],
    [0.04, -0.008],
];
const HINDWING = [
    [0.03, -0.002], [0.09, -0.016], [0.16, -0.036], [0.225, -0.062], [0.275, -0.092],
    [0.306, -0.126], [0.316, -0.162], [0.306, -0.197], [0.284, -0.226], [0.257, -0.248],
    [0.236, -0.262], [0.226, -0.288], [0.22, -0.318], [0.212, -0.342], [0.2, -0.352],
    [0.188, -0.343], [0.184, -0.316], [0.181, -0.288], [0.166, -0.272], [0.134, -0.258],
    [0.1, -0.238], [0.071, -0.204], [0.05, -0.156], [0.038, -0.098], [0.031, -0.045],
];
// Markings drawn inside each wing: dark stripes and margins, then light
// spots in the margins. Shapes may overrun the wing; the outline clips them.
const FORE_MARKS = {
    dark: [
        [[0.05, 0.3], [0.098, 0.3], [0.118, -0.14], [0.072, -0.14]],
        [[0.14, 0.3], [0.176, 0.3], [0.192, 0], [0.16, 0]],
        [[0.222, 0.3], [0.25, 0.3], [0.26, 0.05], [0.236, 0.05]],
        [[0.292, 0.3], [0.314, 0.3], [0.322, 0.09], [0.303, 0.09]],
        [
            [0.4, 0.3], [0.405, 0.15], [0.438, 0.122], [0.452, 0.085], [0.444, 0.048],
            [0.424, 0.012], [0.394, -0.022], [0.356, -0.05], [0.312, -0.066], [0.27, -0.074],
            [0.27, -0.25], [0.7, -0.25], [0.7, 0.3],
        ],
    ],
    spots: [
        [0.47, 0.105, 0.008], [0.47, 0.065, 0.009], [0.455, 0.028, 0.009],
        [0.43, -0.006, 0.009], [0.4, -0.036, 0.009], [0.36, -0.062, 0.008],
    ],
};
const HIND_MARKS = {
    dark: [
        [
            [0.24, -0.062], [0.258, -0.094], [0.264, -0.128], [0.258, -0.162], [0.242, -0.19],
            [0.222, -0.212], [0.2, -0.229], [0.176, -0.242], [0.156, -0.252],
            [0.14, -0.5], [0.6, -0.5], [0.6, -0.05],
        ],
        [[0, 0.02], [0.06, 0], [0.078, -0.09], [0.098, -0.17], [0.124, -0.236], [0.146, -0.29], [0, -0.3]],
    ],
    spots: [
        [0.285, -0.124, 0.009], [0.287, -0.16, 0.009], [0.274, -0.195, 0.009],
        [0.252, -0.222, 0.009], [0.227, -0.242, 0.008],
    ],
};
// The body as [chord, radius] beads, head first.
const BODY = [
    [0.088, 0.017], [0.064, 0.021], [0.04, 0.023], [0.016, 0.022], [-0.01, 0.019],
    [-0.05, 0.017], [-0.095, 0.015], [-0.14, 0.013], [-0.185, 0.011], [-0.228, 0.009],
    [-0.265, 0.006],
];

// view: { W, H, f } in scene pixels, plus hwu/hhu.
export function paintFlock(g, flock, view, t) {
    g.clearRect(0, 0, view.W, view.H);
    const farFirst = [...flock.butterflies].sort((a, b) => b.p.z - a.p.z);
    for (const b of farFirst) paintButterfly(g, b, view, t);
}

function paintButterfly(g, b, view, t) {
    const pose = wingPose(b, t);
    const frame = bodyFrame(b.yaw, pose.pitch, pose.bank);
    const [F, R, U] = [frame.F, frame.R, frame.U].map(tiltTowardViewer);
    const P = [b.p.x, b.p.y + pose.bob, b.p.z];
    const k = b.size;
    const fog = 1 - 0.5 * clamp((b.p.z - Z_NEAR) / (Z_FAR - Z_NEAR), 0, 1);
    const project = (v) => [view.W / 2 + (view.f * v[0]) / v[2], view.H / 2 - (view.f * v[1]) / v[2]];

    // A wing hinges along the body: its span tilts from the body's right (or
    // left) toward its up as the stroke angle rises.
    // Each point's span direction comes from the wing's angle at that span,
    // so a beating wing curves instead of swinging as a flat panel.
    const sides = [1, -1].map((side) => {
        const span = (a) => add(scale(R, side * Math.cos(a)), scale(U, Math.sin(a)));
        const wing = (outline, marks, angle, root, tip) => {
            const at = ([s, c]) => add(P, scale(add(scale(span(angle(s)), s), scale(F, c)), k));
            const a = angle(0.25);
            return { outline, marks, at, root, tip, N: sub(scale(U, Math.cos(a)), scale(R, side * Math.sin(a))) };
        };
        const fore = wing(FOREWING, FORE_MARKS, pose.fore, [0.04, 0.02], [0.42, 0.06]);
        const hind = wing(HINDWING, HIND_MARKS, pose.hind, [0.04, -0.08], [0.27, -0.18]);
        const middle = fore.at([0.25, 0]);
        return { fore, hind, middle, depth: length(middle) };
    });
    sides.sort((a, c) => c.depth - a.depth);

    const paintSide = ({ fore, hind, middle }) => {
        const toCamera = unit(scale(middle, -1));
        // Seen from above, the forewing overlaps the hindwing; from below, the reverse.
        const order = dot(fore.N, toCamera) >= 0 ? [hind, fore] : [fore, hind];
        for (const w of order) {
            const toScreen = (pt) => project(w.at(pt));
            paintWing(g, w, toScreen, shade(w.N, toCamera, fog), (view.f * k) / middle[2]);
        }
    };
    paintSide(sides[0]);
    paintBody(g, { P, F, R, U, k, fog, project, f: view.f });
    paintSide(sides[1]);
}

function paintWing(g, wing, toScreen, light, pxPerUnit) {
    trace(g, wing.outline.map(toScreen));
    // Darker at the root, fuller toward the tip, so a wing reads as a surface.
    const [x0, y0] = toScreen(wing.root), [x1, y1] = toScreen(wing.tip);
    const fill = g.createLinearGradient(x0, y0, x1, y1);
    fill.addColorStop(0, tone(light.base * 0.7));
    fill.addColorStop(0.6, tone(light.base * 0.96));
    fill.addColorStop(1, tone(light.base * 1.06));
    g.fillStyle = fill;
    g.fill();
    g.save();
    g.clip();
    g.fillStyle = tone(light.dark);
    for (const band of wing.marks.dark) {
        trace(g, band.map(toScreen));
        g.fill();
    }
    g.fillStyle = tone(light.light);
    for (const [s, c, r] of wing.marks.spots) {
        const [x, y] = toScreen([s, c]);
        g.beginPath();
        g.arc(x, y, Math.max(0.3, r * pxPerUnit * (0.4 + 0.6 * light.face)), 0, TAU);
        g.fill();
    }
    g.restore();
}

// Light on a wing: lit from the front, glowing through when lit from
// behind, and a brief sheen when it tilts toward the light.
function shade(n, toCamera, fog) {
    const face = dot(n, toCamera);
    const top = face >= 0;
    const nv = top ? n : scale(n, -1);
    const diffuse = Math.max(0, dot(nv, LIGHT));
    const through = Math.max(0, -dot(nv, LIGHT));
    const sheen = Math.max(0, dot(nv, unit(add(LIGHT, toCamera)))) ** 18;
    // Ambient and through-light keep a raised wing from blinking dark each beat.
    const lit = (top ? 1 : 0.9) * (0.4 + 0.55 * diffuse + 0.35 * through) + 0.25 * sheen;
    const base = clamp(lit * fog * 1.05, 0, 1);
    return { base, dark: base * (top ? 0.24 : 0.5), light: Math.min(1, base * 1.12 + 0.04), face: Math.abs(face) };
}

function paintBody(g, { P, F, R, U, k, fog, project, f }) {
    const at = (s, c, u) => add(P, scale(add(add(scale(R, s), scale(F, c)), scale(U, u)), k));
    g.strokeStyle = tone(0.34 * fog);
    g.lineCap = "round";
    const beads = BODY.map(([c, r]) => {
        const w = at(0, c, 0);
        return [...project(w), (r * k * f) / w[2]];
    });
    for (let i = 1; i < beads.length; i++) {
        const [x0, y0, r0] = beads[i - 1], [x1, y1, r1] = beads[i];
        g.lineWidth = Math.max(0.6, r0 + r1);
        g.beginPath();
        g.moveTo(x0, y0);
        g.lineTo(x1, y1);
        g.stroke();
    }
    g.fillStyle = tone(0.34 * fog);
    g.strokeStyle = tone(0.4 * fog);
    g.lineWidth = Math.max(0.4, (0.006 * k * f) / P[2]);
    for (const side of [1, -1]) {
        const [a, m, tip] = [[0.006, 0.09, 0.006], [0.026, 0.18, 0.026], [0.05, 0.26, 0.046]]
            .map(([s, c, u]) => project(at(side * s, c, u)));
        g.beginPath();
        g.moveTo(a[0], a[1]);
        g.quadraticCurveTo(m[0], m[1], tip[0], tip[1]);
        g.stroke();
        g.beginPath();
        g.arc(tip[0], tip[1], Math.max(0.4, (0.009 * k * f) / P[2]), 0, TAU);
        g.fill();
    }
}

// ── Small helpers ──────────────────────────────────────────────────────

function bodyFrame(yaw, pitch, bank) {
    const d = [Math.cos(yaw), 0, Math.sin(yaw)];
    const right = [Math.sin(yaw), 0, -Math.cos(yaw)];
    const F = add(scale(d, Math.cos(pitch)), [0, Math.sin(pitch), 0]);
    const up = add(scale(d, -Math.sin(pitch)), [0, Math.cos(pitch), 0]);
    // bank > 0 lowers the right wing.
    const R = sub(scale(right, Math.cos(bank)), scale(up, Math.sin(bank)));
    const U = add(scale(up, Math.cos(bank)), scale(right, Math.sin(bank)));
    return { F, R, U };
}

// Rotates about the x axis so a body's up leans toward the camera.
function tiltTowardViewer([x, y, z]) {
    const c = Math.cos(VIEW_TILT), s = Math.sin(VIEW_TILT);
    return [x, y * c + z * s, z * c - y * s];
}

// A closed Catmull-Rom curve through the points: outlines without corners.
function trace(g, pts) {
    const n = pts.length;
    g.beginPath();
    g.moveTo(pts[0][0], pts[0][1]);
    for (let i = 0; i < n; i++) {
        const p0 = pts[(i + n - 1) % n], p1 = pts[i], p2 = pts[(i + 1) % n], p3 = pts[(i + 2) % n];
        g.bezierCurveTo(
            p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6,
            p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6,
            p2[0], p2[1],
        );
    }
    g.closePath();
}

// Bone, darkened to brightness v.
function tone(v) {
    const k = clamp(v, 0, 1);
    return `rgb(${Math.round(BONE[0] * k)},${Math.round(BONE[1] * k)},${Math.round(BONE[2] * k)})`;
}

function add(a, b) { return [a[0] + b[0], a[1] + b[1], a[2] + b[2]]; }
function sub(a, b) { return [a[0] - b[0], a[1] - b[1], a[2] - b[2]]; }
function scale(a, s) { return [a[0] * s, a[1] * s, a[2] * s]; }
function dot(a, b) { return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]; }
function length(a) { return Math.hypot(a[0], a[1], a[2]); }
function unit(a) { return scale(a, 1 / length(a)); }

function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }
function smoothstep(a, b, v) {
    const x = clamp((v - a) / (b - a), 0, 1);
    return x * x * (3 - 2 * x);
}
// Signed shortest turn from one heading to another, in [-PI, PI).
function turnToward(from, to) {
    return ((((to - from) % TAU) + TAU * 1.5) % TAU) - Math.PI;
}
// Smooth, bounded drift in [-1, 1] from incommensurate sines.
function noise(t, s) {
    return 0.5 * Math.sin(t + s) + 0.3 * Math.sin(1.73 * t + 2.3 * s) + 0.2 * Math.sin(2.91 * t + 4.1 * s);
}
function between(rand, lo, hi) { return lo + rand() * (hi - lo); }
function mulberry32(seed) {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6d2b79f5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
